import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { supabase } from '../../api/supabase'
import { parseDocumentWithAI } from '../../api/documentParser'

type Step = 'upload' | 'parsing' | 'review' | 'creating' | 'done'

interface ParsedMBLData {
  doc_type: 'MBL' | 'HBL'
  mbl_number?: string
  hbl_number?: string
  shipper_name?: string
  shipper_address?: string
  consignee_name?: string
  consignee_address?: string
  notify_party?: string
  port_of_loading?: string
  port_of_discharge?: string
  vessel_name?: string
  voyage_number?: string
  container_numbers?: string[]
  goods_description?: string
  total_weight_kg?: number
  total_volume_cbm?: number
  eta?: string
  etd?: string
}

export default function ImportDocumentPage() {
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [step, setStep] = useState<Step>('upload')
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState<string>('')
  const [progress, setProgress] = useState<string>('')
  const [docWarning, setDocWarning] = useState<string>('')

  const [parsedData, setParsedData] = useState<ParsedMBLData | null>(null)
  const [editedData, setEditedData] = useState<ParsedMBLData | null>(null)

  const editData = editedData || parsedData

  async function handleParse() {
    if (!file) return

    setError('')
    setStep('parsing')
    setProgress('Uploading and parsing document...')

    try {
      // Read file as base64
      const reader = new FileReader()
      reader.onload = async () => {
        try {
          const base64 = (reader.result as string).split(',')[1]
          setProgress('Extracting text from PDF...')

          const result = await parseDocumentWithAI(base64)

          if (!result) {
            throw new Error('Failed to parse document')
          }

          setParsedData(result)
          setEditedData(null)
          setStep('review')
          setProgress('')
        } catch (err) {
          const errMsg = err instanceof Error ? err.message : 'Parse failed'
          setError(errMsg)
          setStep('upload')
          setProgress('')
        }
      }
      reader.readAsDataURL(file)
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Error reading file'
      setError(errMsg)
      setStep('upload')
      setProgress('')
    }
  }

  async function handleCreateJob() {
    if (!editData) return

    setStep('creating')
    setProgress('Creating job from parsed data...')
    setError('')
    setDocWarning('')

    try {
      // Create job with all extracted fields
      const jobRef = `${editData.doc_type}-${editData.mbl_number || editData.hbl_number || Date.now()}`

      const { data: job, error: jobError } = await supabase
        .from('jobs')
        .insert({
          job_ref: jobRef,
          direction: 'IMP', // Default to Import for documents
          transport_mode: 'SEA', // Default to Sea for MBL/HBL
          // K4: Customs Team, not Pre-Alert -- matches the AutoJobs default fixed in #10/#299.
          status: 'CUS',
          shipper: editData.shipper_name,
          shipper_address: editData.shipper_address,
          consignee: editData.consignee_name,
          consignee_address: editData.consignee_address,
          notify_party: editData.notify_party,
          port_of_loading: editData.port_of_loading,
          port_of_discharge: editData.port_of_discharge,
          vessel_flight: editData.vessel_name,
          voyage_number: editData.voyage_number,
          container_numbers: editData.container_numbers?.join(','),
          goods_description: editData.goods_description,
          total_weight_kg: editData.total_weight_kg,
          total_volume_cbm: editData.total_volume_cbm,
          eta: editData.eta,
          etd: editData.etd,
          mbl_number: editData.mbl_number,
          hbl_number: editData.hbl_number,
          import_source: 'DOCUMENT',
          created_at: new Date().toISOString()
        })
        .select()
        .single()

      if (jobError) throw jobError

      // Store the uploaded document as a job document. Job creation above already succeeded
      // (jobError threw if not) -- a failure here is downgraded to a warning rather than
      // failing the whole flow, but it must never be silent: staff needs to know the source
      // MBL/HBL was NOT attached to the job they were just told was created successfully.
      let docFailureMsg = ''
      if (file && job) {
        const fileName = `${job.id}/MBL/${Date.now()}_${file.name}`
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('job-documents')
          .upload(fileName, file)

        if (uploadError) {
          Sentry.captureException(uploadError, { tags: { feature: 'import_document_upload' }, extra: { jobId: job.id } })
          docFailureMsg = `Job created, but the source document failed to attach: ${uploadError.message}. Please attach it manually from the job.`
        } else if (uploadData) {
          const { data: urlData } = supabase.storage
            .from('job-documents')
            .getPublicUrl(uploadData.path)

          const { error: docError } = await supabase
            .from('job_documents')
            .insert({
              job_id: job.id,
              doc_type: editData.doc_type,
              file_name: file.name,
              file_url: urlData.publicUrl,
              file_size: file.size,
              mime_type: file.type,
              ai_parsed: true,
              ai_result: editData
            })

          if (docError) {
            Sentry.captureException(docError, { tags: { feature: 'import_document_record' }, extra: { jobId: job.id } })
            docFailureMsg = `Job created, but the source document failed to attach: ${docError.message}. Please attach it manually from the job.`
          }
        }
      }

      if (docFailureMsg) setDocWarning(docFailureMsg)
      setStep('done')
      setProgress('')

      // Redirect to job detail -- longer delay when there's a warning to read, so the
      // auto-redirect doesn't whisk staff past it before they notice.
      setTimeout(() => {
        navigate(`/jobs/${job.id}`)
      }, docFailureMsg ? 6000 : 2000)
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Failed to create job'
      setError(errMsg)
      setStep('review')
      setProgress('')
    }
  }

  function updateField(field: keyof ParsedMBLData, value: any) {
    if (!editData) return
    setEditedData({
      ...editData,
      [field]: value
    })
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/jobs')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 mb-4"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Jobs
          </button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Import from MBL/HBL</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">Upload a document to create a new job with extracted data</p>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center gap-2 mb-8">
          {(['upload', 'parsing', 'review', 'creating', 'done'] as Step[]).map((s, i) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step === s ? 'bg-blue-600 text-white' :
                (['parsing', 'review', 'creating', 'done'] as Step[]).indexOf(step) > i ? 'bg-green-500 text-white' :
                'bg-gray-200 dark:bg-gray-700 text-gray-500'
              }`}>
                {i + 1}
              </div>
              {i < 4 && <div className="w-8 h-0.5 bg-gray-200 dark:bg-gray-700" />}
            </div>
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
            {error}
          </div>
        )}

        {/* Step 1: Upload */}
        {step === 'upload' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault()
                const f = e.dataTransfer.files[0]
                if (f?.type === 'application/pdf') setFile(f)
              }}
              className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-12 text-center cursor-pointer hover:border-blue-400 transition-colors"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
              />
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-xl font-semibold text-gray-700 dark:text-gray-300">
                {file ? file.name : 'Drop PDF file here or click to upload'}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">MBL or HBL documents only (PDF)</p>
            </div>

            {file && (
              <button
                onClick={handleParse}
                className="mt-6 w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
              >
                Parse Document with AI
              </button>
            )}
          </div>
        )}

        {/* Step 2: Parsing */}
        {step === 'parsing' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-700 dark:text-gray-300 font-medium">{progress || 'Processing...'}</p>
          </div>
        )}

        {/* Step 3: Review */}
        {step === 'review' && editData && (
          <div className="space-y-6">
            {/* Document Type */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Document Type</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Document Type</label>
                  <input
                    type="text"
                    value={editData.doc_type || ''}
                    onChange={(e) => updateField('doc_type', e.target.value as 'MBL' | 'HBL')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Number</label>
                  <input
                    type="text"
                    value={editData.mbl_number || editData.hbl_number || ''}
                    onChange={(e) => updateField(editData.doc_type === 'MBL' ? 'mbl_number' : 'hbl_number', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            </div>

            {/* Parties */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Parties</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Shipper Name</label>
                  <input
                    type="text"
                    value={editData.shipper_name || ''}
                    onChange={(e) => updateField('shipper_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Shipper Address</label>
                  <textarea
                    value={editData.shipper_address || ''}
                    onChange={(e) => updateField('shipper_address', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Consignee Name</label>
                  <input
                    type="text"
                    value={editData.consignee_name || ''}
                    onChange={(e) => updateField('consignee_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Consignee Address</label>
                  <textarea
                    value={editData.consignee_address || ''}
                    onChange={(e) => updateField('consignee_address', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notify Party</label>
                  <input
                    type="text"
                    value={editData.notify_party || ''}
                    onChange={(e) => updateField('notify_party', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            </div>

            {/* Ports & Vessel */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Ports & Vessel</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Port of Loading</label>
                  <input
                    type="text"
                    value={editData.port_of_loading || ''}
                    onChange={(e) => updateField('port_of_loading', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Port of Discharge</label>
                  <input
                    type="text"
                    value={editData.port_of_discharge || ''}
                    onChange={(e) => updateField('port_of_discharge', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Vessel Name</label>
                  <input
                    type="text"
                    value={editData.vessel_name || ''}
                    onChange={(e) => updateField('vessel_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Voyage Number</label>
                  <input
                    type="text"
                    value={editData.voyage_number || ''}
                    onChange={(e) => updateField('voyage_number', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            </div>

            {/* Cargo Details */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Cargo Details</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Goods Description</label>
                  <textarea
                    value={editData.goods_description || ''}
                    onChange={(e) => updateField('goods_description', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Total Weight (kg)</label>
                    <input
                      type="number"
                      value={editData.total_weight_kg || ''}
                      onChange={(e) => updateField('total_weight_kg', e.target.value ? Number(e.target.value) : null)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Total Volume (CBM)</label>
                    <input
                      type="number"
                      value={editData.total_volume_cbm || ''}
                      onChange={(e) => updateField('total_volume_cbm', e.target.value ? Number(e.target.value) : null)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Dates */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Dates</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">ETD (Estimated Time of Departure)</label>
                  <input
                    type="date"
                    value={editData.etd || ''}
                    onChange={(e) => updateField('etd', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">ETA (Estimated Time of Arrival)</label>
                  <input
                    type="date"
                    value={editData.eta || ''}
                    onChange={(e) => updateField('eta', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4">
              <button
                onClick={() => setStep('upload')}
                className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleCreateJob}
                className="flex-1 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors"
              >
                Create Job
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Creating */}
        {step === 'creating' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
            <div className="w-12 h-12 border-4 border-green-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-700 dark:text-gray-300 font-medium">{progress || 'Creating job...'}</p>
          </div>
        )}

        {/* Step 5: Done */}
        {step === 'done' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-xl font-semibold text-gray-900 dark:text-white">Job created successfully!</p>
            <p className="text-gray-500 dark:text-gray-400 mt-2">Redirecting to job detail...</p>
            {docWarning && (
              <p className="mt-4 text-sm text-amber-700 dark:text-amber-400 max-w-md mx-auto">{docWarning}</p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
